/**
 * @zendir/ui - MissionClock Component (Pure React)
 *
 * Advanced mission time display following Astro UX patterns with full
 * space operations support.
 *
 * Features:
 * - Theme-integrated via useTheme()
 * - UTC/Zulu time display
 * - Configurable formats (12h, 24h, ISO)
 * - Optional seconds display
 * - AOS/LOS timer support (Acquisition/Loss of Signal)
 * - MET (Mission Elapsed Time) display
 * - Julian day format (DOY-###)
 * - Simulation time support (custom epoch/time source)
 * - Tabular numbers for stability
 * - Zendir SDK integration for real-time data
 *
 * Space Operations Terminology:
 * - AOS: Acquisition of Signal - when satellite rises above horizon
 * - LOS: Loss of Signal - when satellite passes below horizon
 * - TCA: Time of Closest Approach - maximum elevation point
 * - MET: Mission Elapsed Time - time since launch (dd:hh:mm:ss)
 * - DOY: Day of Year - Julian day format
 */

import React, { memo, useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../theme";
import { classNames, tabularNumsStyle } from "../utils";

// ============================================================================
// Types
// ============================================================================

export type ClockFormat = "12h" | "24h" | "iso" | "met";
export type DateFormat = "iso" | "julian" | "gregorian" | "none";
export type TimeSource = "realtime" | "simulation" | "custom";

/**
 * Timer configuration for AOS/LOS countdown/up timers
 */
export interface TimerConfig {
  /** Timer label (e.g., "AOS", "LOS", "MET") */
  label: string;
  /** Target timestamp (for countdown) or start timestamp (for countup) */
  timestamp: Date;
  /** Timer direction: 'countdown' counts to target, 'countup' counts from start */
  direction: "countdown" | "countup";
  /** Optional status color */
  status?: "normal" | "caution" | "critical" | "standby";
  /** Show days in timer format */
  showDays?: boolean;
}

/**
 * Simulation time configuration
 */
export interface SimulationTimeConfig {
  /** Simulation epoch (t=0) as Date or ISO string */
  epoch: Date | string;
  /** Current simulation time offset in seconds from epoch */
  elapsedSeconds?: number;
  /** Time scale factor (1 = realtime, 2 = 2x speed, etc.) */
  timeScale?: number;
  /** Whether simulation is running */
  isRunning?: boolean;
  /** Callback to get current simulation time */
  getCurrentTime?: () => Date;
}

export interface MissionClockProps {
  /** Clock format */
  format?: ClockFormat;
  /** Timezone label to display */
  timezone?: string;
  /** Hide seconds */
  hideSeconds?: boolean;
  /** Small size variant */
  small?: boolean;
  /** Show date */
  showDate?: boolean;
  /** Date format (iso, julian, gregorian) */
  dateFormat?: DateFormat;
  /** Custom className */
  className?: string;
  /** Click handler */
  onClick?: () => void;

  // === AOS/LOS Timer Support ===
  /** AOS timer - countdown/up to Acquisition of Signal */
  aosTimer?: TimerConfig | Date;
  /** LOS timer - countdown/up to Loss of Signal */
  losTimer?: TimerConfig | Date;
  /** Additional custom timers */
  customTimers?: TimerConfig[];

  // === Simulation Time Support ===
  /** Time source: realtime, simulation, or custom */
  timeSource?: TimeSource;
  /** Simulation time configuration (for timeSource='simulation') */
  simulationConfig?: SimulationTimeConfig;
  /** Custom time provider (for timeSource='custom') */
  customTime?: Date | (() => Date);

  // === Mission Elapsed Time ===
  /** Mission start time for MET display */
  missionStart?: Date | string;
  /** Show MET (Mission Elapsed Time) instead of wall clock */
  showMET?: boolean;

  // === Advanced Options ===
  /** Update interval in milliseconds (default: 1000) */
  updateInterval?: number;
  /** Compact mode - single line display */
  compact?: boolean;
  /** Show milliseconds */
  showMilliseconds?: boolean;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format duration as dd:hh:mm:ss (MET format)
 */
function formatMET(ms: number, showMilliseconds = false): string {
  const totalSeconds = Math.floor(Math.abs(ms) / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const sign = ms < 0 ? "-" : "";

  if (showMilliseconds) {
    const millis = Math.floor(Math.abs(ms) % 1000);
    return `${sign}${days.toString().padStart(2, "0")}:${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${millis.toString().padStart(3, "0")}`;
  }

  return `${sign}${days.toString().padStart(2, "0")}:${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Format duration for timers (compact: hh:mm:ss or mm:ss)
 */
function formatTimerDuration(ms: number, showDays = false): string {
  const totalSeconds = Math.floor(Math.abs(ms) / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const sign = ms < 0 ? "-" : "";

  if (showDays && days > 0) {
    return `${sign}${days}d ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  if (hours > 0 || showDays) {
    return `${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  return `${sign}${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Get Julian day (day of year 001-366)
 */
function getJulianDay(date: Date): string {
  const start = new Date(Date.UTC(date.getUTCFullYear(), 0, 0));
  const diff = date.getTime() - start.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const day = Math.floor(diff / oneDay);
  return day.toString().padStart(3, "0");
}

/**
 * Normalize timer config from Date or TimerConfig
 */
function normalizeTimerConfig(
  timer: TimerConfig | Date | undefined,
  label: string,
): TimerConfig | undefined {
  if (!timer) return undefined;
  if (timer instanceof Date) {
    return {
      label,
      timestamp: timer,
      direction: "countdown",
    };
  }
  return timer;
}

// ============================================================================
// Timer Sub-component
// ============================================================================

interface TimerDisplayProps {
  timer: TimerConfig;
  currentTime: Date;
  tokens: ReturnType<typeof useTheme>["tokens"];
  compact?: boolean;
}

const TimerDisplay = memo(function TimerDisplay({
  timer,
  currentTime,
  tokens,
  compact = false,
}: TimerDisplayProps) {
  const diff =
    timer.direction === "countdown"
      ? timer.timestamp.getTime() - currentTime.getTime()
      : currentTime.getTime() - timer.timestamp.getTime();

  const isNegative = diff < 0;
  const statusColor = timer.status
    ? tokens.colors.status[timer.status]
    : isNegative
      ? tokens.colors.status.caution
      : tokens.colors.status.normal;

  return (
    <div
      style={{
        display: "flex",
        alignItems: compact ? "center" : "flex-start",
        flexDirection: compact ? "row" : "column",
        gap: compact ? 8 : 2,
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
        backgroundColor: `${statusColor}15`,
        borderRadius: tokens.borderRadius.sm,
        border: `1px solid ${statusColor}30`,
      }}
    >
      <span
        style={{
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: tokens.typography.fontWeight.semibold,
          color: statusColor,
          textTransform: "uppercase",
          letterSpacing: tokens.typography.letterSpacing.wide,
        }}
      >
        {timer.label}
      </span>
      <span
        style={{
          fontSize: compact
            ? tokens.typography.fontSize.sm
            : tokens.typography.fontSize.base,
          fontWeight: tokens.typography.fontWeight.bold, // Bold (700) for timer readability
          fontFamily: tokens.typography.fontFamily.mono,
          color: statusColor,
          ...tabularNumsStyle,
        }}
      >
        {isNegative ? "+" : ""}
        {formatTimerDuration(diff, timer.showDays)}
      </span>
    </div>
  );
});

// ============================================================================
// Main Component
// ============================================================================

/**
 * MissionClock - Advanced mission time display for space operations
 *
 * Supports real-time clock, simulation time, MET (Mission Elapsed Time),
 * and AOS/LOS timers following Astro UX Design System patterns.
 *
 * @example
 * ```tsx
 * // Basic clock
 * <MissionClock format="24h" timezone="UTC" />
 *
 * // With AOS/LOS timers
 * <MissionClock
 *   format="24h"
 *   aosTimer={{ label: 'AOS', timestamp: new Date('2024-03-15T10:30:00Z'), direction: 'countdown' }}
 *   losTimer={{ label: 'LOS', timestamp: new Date('2024-03-15T10:45:00Z'), direction: 'countdown' }}
 * />
 *
 * // Simulation time
 * <MissionClock
 *   timeSource="simulation"
 *   simulationConfig={{
 *     epoch: new Date('2026-01-01T00:00:00Z'),
 *     elapsedSeconds: 3600,
 *     timeScale: 1,
 *   }}
 * />
 *
 * // Mission Elapsed Time
 * <MissionClock
 *   showMET
 *   missionStart={new Date('2026-01-01T00:00:00Z')}
 * />
 *
 * // Julian day format
 * <MissionClock showDate dateFormat="julian" />
 * ```
 */
export const MissionClock = memo(function MissionClock({
  format = "24h",
  timezone = "UTC",
  hideSeconds = false,
  small = false,
  showDate = false,
  dateFormat = "iso",
  className = "",
  onClick,
  aosTimer,
  losTimer,
  customTimers = [],
  timeSource = "realtime",
  simulationConfig,
  customTime,
  missionStart,
  showMET = false,
  updateInterval = 1000,
  compact = false,
  showMilliseconds = false,
}: MissionClockProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";

  // State for current time
  const [time, setTime] = useState(() => getCurrentTimeFromSource());

  // Normalize timer configs
  const normalizedAOS = useMemo(
    () => normalizeTimerConfig(aosTimer, "AOS"),
    [aosTimer],
  );
  const normalizedLOS = useMemo(
    () => normalizeTimerConfig(losTimer, "LOS"),
    [losTimer],
  );

  // All timers combined
  const allTimers = useMemo(() => {
    const timers: TimerConfig[] = [];
    if (normalizedAOS) timers.push(normalizedAOS);
    if (normalizedLOS) timers.push(normalizedLOS);
    return [...timers, ...customTimers];
  }, [normalizedAOS, normalizedLOS, customTimers]);

  // Get current time based on source
  function getCurrentTimeFromSource(): Date {
    switch (timeSource) {
      case "simulation":
        if (simulationConfig) {
          if (simulationConfig.getCurrentTime) {
            return simulationConfig.getCurrentTime();
          }
          const epoch =
            typeof simulationConfig.epoch === "string"
              ? new Date(simulationConfig.epoch)
              : simulationConfig.epoch;
          const elapsed = (simulationConfig.elapsedSeconds || 0) * 1000;
          return new Date(epoch.getTime() + elapsed);
        }
        return new Date();

      case "custom":
        if (customTime) {
          return typeof customTime === "function" ? customTime() : customTime;
        }
        return new Date();

      case "realtime":
      default:
        return new Date();
    }
  }

  // Format time display
  const formatTimeDisplay = useCallback(
    (date: Date): string => {
      if (showMET && missionStart) {
        const start =
          typeof missionStart === "string"
            ? new Date(missionStart)
            : missionStart;
        const elapsed = date.getTime() - start.getTime();
        return formatMET(elapsed, showMilliseconds);
      }

      if (format === "met" && missionStart) {
        const start =
          typeof missionStart === "string"
            ? new Date(missionStart)
            : missionStart;
        const elapsed = date.getTime() - start.getTime();
        return formatMET(elapsed, showMilliseconds);
      }

      const hours = date.getUTCHours();
      const mins = date.getUTCMinutes();
      const secs = date.getUTCSeconds();
      const millis = date.getUTCMilliseconds();

      let hoursStr: string;
      let suffix = "";

      if (format === "12h") {
        const h12 = hours % 12 || 12;
        hoursStr = h12.toString().padStart(2, "0");
        suffix = hours >= 12 ? " PM" : " AM";
      } else {
        hoursStr = hours.toString().padStart(2, "0");
      }

      const minsStr = mins.toString().padStart(2, "0");
      const secsStr = secs.toString().padStart(2, "0");
      const millisStr = millis.toString().padStart(3, "0");

      if (hideSeconds) {
        return `${hoursStr}:${minsStr}${suffix}`;
      }

      if (showMilliseconds) {
        return `${hoursStr}:${minsStr}:${secsStr}.${millisStr}${suffix}`;
      }

      return `${hoursStr}:${minsStr}:${secsStr}${suffix}`;
    },
    [format, hideSeconds, showMET, missionStart, showMilliseconds],
  );

  // Format date display
  const formatDateDisplay = useCallback(
    (date: Date): string => {
      switch (dateFormat) {
        case "julian": {
          const year = date.getUTCFullYear();
          const doy = getJulianDay(date);
          return `${year}/${doy}`;
        }
        case "gregorian": {
          const year = date.getUTCFullYear();
          const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
          const day = date.getUTCDate().toString().padStart(2, "0");
          return `${month}/${day}/${year}`;
        }
        case "iso":
        default:
          if (format === "iso") {
            return date.toISOString().split("T")[0];
          }
          const year = date.getUTCFullYear();
          const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
          const day = date.getUTCDate().toString().padStart(2, "0");
          return `${year}-${month}-${day}`;
      }
    },
    [format, dateFormat],
  );

  // Update time at interval
  useEffect(() => {
    // Calculate effective update interval
    const effectiveInterval = showMilliseconds
      ? Math.min(updateInterval, 100)
      : hideSeconds
        ? 60000
        : updateInterval;

    const intervalId = setInterval(() => {
      setTime(getCurrentTimeFromSource());
    }, effectiveInterval);

    return () => clearInterval(intervalId);
  }, [
    hideSeconds,
    updateInterval,
    showMilliseconds,
    timeSource,
    simulationConfig,
    customTime,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  // Determine display label
  const displayLabel = useMemo(() => {
    if (showMET || format === "met") {
      return "MET";
    }
    if (timeSource === "simulation") {
      return "SIM";
    }
    return timezone;
  }, [showMET, format, timeSource, timezone]);

  return (
    <div
      className={classNames("zendir-mission-clock", className)}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role="timer"
      aria-label={`Current time: ${formatTimeDisplay(time)} ${displayLabel}`}
      style={{
        display: "inline-flex",
        flexDirection: compact ? "row" : "column",
        alignItems: "center",
        gap: compact ? 16 : 4,
        padding: small
          ? `${tokens.spacing.xs} ${tokens.spacing.sm}`
          : `${tokens.spacing.sm} ${tokens.spacing.md}`,
        backgroundColor: isTransparentTheme
          ? "transparent"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)" as const,
        }),
        ...(tokens.colors.border.cardStyle ?? {
          border: `1px solid ${tokens.colors.border.muted}`,
        }),
        borderRadius: tokens.borderRadius.md,
        cursor: onClick ? "pointer" : "default",
        outline: "none",
        transition: tokens.animation.fast,
        ...tabularNumsStyle,
      }}
    >
      {/* Main clock section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Date (optional) */}
        {showDate && dateFormat !== "none" && (
          <span
            style={{
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.normal,
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.text.tertiary,
              letterSpacing: tokens.typography.letterSpacing.normal,
            }}
          >
            {formatDateDisplay(time)}
          </span>
        )}

        {/* Time - Using bold weight for critical time displays (Apple/Google best practice) */}
        <span
          style={{
            fontSize: small
              ? tokens.typography.fontSize.base
              : tokens.typography.fontSize.xl,
            fontWeight: tokens.typography.fontWeight.bold, // Bold (700) for readability - time is critical info
            fontFamily: tokens.typography.fontFamily.mono,
            letterSpacing: tokens.typography.letterSpacing.normal,
            color: tokens.colors.text.primary,
          }}
        >
          {formatTimeDisplay(time)}
        </span>

        {/* Label (timezone/MET/SIM) */}
        {displayLabel && (
          <span
            style={{
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.medium,
              color:
                timeSource === "simulation"
                  ? tokens.colors.status.caution
                  : tokens.colors.text.tertiary,
              textTransform: "uppercase",
              letterSpacing: tokens.typography.letterSpacing.wide,
            }}
          >
            {displayLabel}
          </span>
        )}
      </div>

      {/* AOS/LOS Timers */}
      {allTimers.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: compact ? "row" : "column",
            gap: 8,
          }}
        >
          {allTimers.map((timer, index) => (
            <TimerDisplay
              key={`${timer.label}-${index}`}
              timer={timer}
              currentTime={time}
              tokens={tokens}
              compact={compact}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// ============================================================================
// Convenience Components
// ============================================================================

/**
 * SimpleTimer - Standalone countdown/up timer
 */
export interface SimpleTimerProps {
  /** Timer label */
  label: string;
  /** Target/start timestamp */
  timestamp: Date;
  /** Timer direction */
  direction?: "countdown" | "countup";
  /** Status color */
  status?: "normal" | "caution" | "critical" | "standby";
  /** Show days */
  showDays?: boolean;
  /** Custom className */
  className?: string;
}

export const SimpleTimer = memo(function SimpleTimer({
  label,
  timestamp,
  direction = "countdown",
  status,
  showDays = false,
  className = "",
}: SimpleTimerProps): React.ReactElement {
  const { tokens } = useTheme();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const timer: TimerConfig = { label, timestamp, direction, status, showDays };

  return (
    <div className={classNames("zendir-simple-timer", className)}>
      <TimerDisplay timer={timer} currentTime={now} tokens={tokens} />
    </div>
  );
});

/**
 * MissionElapsedTime - Dedicated MET display
 */
export interface MissionElapsedTimeProps {
  /** Mission launch/start time */
  launchTime: Date | string;
  /** Show milliseconds */
  showMilliseconds?: boolean;
  /** Custom className */
  className?: string;
  /** Small variant */
  small?: boolean;
}

export const MissionElapsedTime = memo(function MissionElapsedTime({
  launchTime,
  showMilliseconds = false,
  className = "",
  small = false,
}: MissionElapsedTimeProps): React.ReactElement {
  return (
    <MissionClock
      format="met"
      missionStart={launchTime}
      showMET
      showMilliseconds={showMilliseconds}
      className={className}
      small={small}
      timezone=""
    />
  );
});

/**
 * SimulationClock - Clock driven by simulation time
 */
export interface SimulationClockProps {
  /** Simulation epoch */
  epoch: Date | string;
  /** Elapsed seconds from epoch */
  elapsedSeconds?: number;
  /** Time scale factor */
  timeScale?: number;
  /** Whether simulation is running */
  isRunning?: boolean;
  /** Show date */
  showDate?: boolean;
  /** Date format */
  dateFormat?: DateFormat;
  /** Custom className */
  className?: string;
  /** Small variant */
  small?: boolean;
}

export const SimulationClock = memo(function SimulationClock({
  epoch,
  elapsedSeconds = 0,
  timeScale = 1,
  isRunning = true,
  showDate = true,
  dateFormat = "julian",
  className = "",
  small = false,
}: SimulationClockProps): React.ReactElement {
  return (
    <MissionClock
      timeSource="simulation"
      simulationConfig={{
        epoch,
        elapsedSeconds,
        timeScale,
        isRunning,
      }}
      showDate={showDate}
      dateFormat={dateFormat}
      className={className}
      small={small}
    />
  );
});

export default MissionClock;
